import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import TournamentCard from '@/components/TournamentCard';
import { TournamentsListEmpty, TournamentsListView } from '@/components/TournamentsListView';
import { InlineMessage } from '@/components/ui/InlineMessage';
import { useTournamentsListContext } from '@/layouts/TournamentsListLayout/context';
import { apiClient } from '@/services/api';
import { getDateLocale } from '@/utils/dateLocale';
import { TOURNAMENT_ROLES } from '@/constants';
import type { Tournament } from '@/types';

const ArchivedTournamentsTab: React.FC = () => {
  const { t } = useTranslation(['tournament']);
  const queryClient = useQueryClient();
  const { activeTab } = useTournamentsListContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [unarchivingId, setUnarchivingId] = useState<number | null>(null);

  const { mutate: unarchive } = useMutation({
    mutationFn: (tournamentId: number) => apiClient.unarchiveTournament(tournamentId),
    onMutate: (tournamentId: number) => {
      setErrorMessage(null);
      setUnarchivingId(tournamentId);
    },
    onSuccess: (_data, tournamentId) => {
      queryClient.invalidateQueries({ queryKey: ['archived-tournaments'] });
      queryClient.invalidateQueries({ queryKey: ['my-tournaments'] });
      queryClient.invalidateQueries({ queryKey: ['next-to-predict'] });
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            (key[0] === 'tournament' || key[0] === 'tournament-matches') &&
            String(key[1]) === String(tournamentId)
          );
        },
      });
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.error || t('tournament:errorUnarchivingTournament'));
    },
    onSettled: () => {
      setUnarchivingId(null);
    },
  });

  const renderCard = useCallback((tournament: Tournament) => {
    const isHolder = tournament.userRole === TOURNAMENT_ROLES.HOLDER;
    const archivedDate = tournament.archivedAt
      ? format(new Date(tournament.archivedAt), 'd MMM yyyy', { locale: getDateLocale() })
      : null;

    return (
      <TournamentCard
        key={tournament.id}
        tournament={tournament}
        showUserStats
        showStatus={false}
        action={isHolder ? (
          <button
            type="button"
            className="ls-btn ls-btn--outlined"
            data-testid="unarchive-tournament-btn"
            disabled={unarchivingId === tournament.id}
            onClick={() => unarchive(tournament.id)}
          >
            {unarchivingId === tournament.id ? t('tournament:unarchiving') : t('tournament:unarchive')}
          </button>
        ) : undefined}
        meta={archivedDate ? t('tournament:archivedOn', { date: archivedDate }) : undefined}
      />
    );
  }, [t, unarchivingId, unarchive]);

  return (
    <TournamentsListView
      source="archived"
      isActive={activeTab === 'archived'}
      title={t('tournament:archivedTournaments')}
      renderCard={renderCard}
      filterSet="seasonRole"
      message={errorMessage
        ? <InlineMessage type="error" text={errorMessage} onDismiss={() => setErrorMessage(null)} />
        : undefined}
      emptyState={(
        <TournamentsListEmpty
          title={t('tournament:noArchivedTournaments')}
          text={t('tournament:noArchivedTournamentsText')}
          actions={(
            <Link to="/tournaments" className="ls-btn ls-btn--outlined">
              {t('tournament:tournaments')}
            </Link>
          )}
        />
      )}
    />
  );
};

export default ArchivedTournamentsTab;
